import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkle, X, ArrowRight, Warning } from "@phosphor-icons/react";

/**
 * Slim app-wide banner that shows the trial countdown until billing_status
 * flips to "active". Hides itself if:
 *  - user is not an admin
 *  - subscription summary is unreachable
 *  - billing_status is anything other than "trial"
 *  - user manually dismissed it for the session
 *  - already on /app/billing (avoid double-nudge)
 */
export default function TrialCountdownStrip() {
  const { user } = useAuth();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && window.sessionStorage.getItem("zenith-trial-dismissed") === "1"
  );

  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);

  useEffect(() => {
    if (!isAdmin) { setSummary(null); return; }
    api.get("/subscription/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [isAdmin, user?.tenant_id]);

  if (!isAdmin || !summary || summary.billing_status !== "trial" || dismissed) return null;
  if (location.pathname.startsWith("/app/billing")) return null;

  const trialEnd = summary.trial_expiry ? new Date(summary.trial_expiry) : null;
  const daysLeft = trialEnd ? Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const urgent = daysLeft !== null && daysLeft <= 5;

  const dismiss = () => {
    setDismissed(true);
    try { window.sessionStorage.setItem("zenith-trial-dismissed", "1"); } catch (_) {}
  };

  const label =
    daysLeft === null ? "You're on a free trial"
    : daysLeft > 1 ? `${daysLeft} days left in your free trial`
    : daysLeft === 1 ? "Last day of your free trial"
    : "Your trial has ended";

  return (
    <div
      data-testid="trial-countdown-strip"
      className={`relative border-b text-white text-xs md:text-sm ${
        urgent
          ? "bg-gradient-to-r from-zenith-magenta via-adb-navy to-zenith-magenta border-zenith-magenta/60"
          : "bg-gradient-to-r from-adb-navy via-zenith-blue to-adb-navy border-adb-navy-800"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {urgent
            ? <Warning size={14} weight="fill" className="text-zenith-green shrink-0" />
            : <Sparkle size={14} weight="fill" className="text-zenith-green shrink-0" />
          }
          <span className="font-semibold truncate">
            {label}
          </span>
          <span className="hidden md:inline text-white/70">
            · You'll keep Enterprise features. Add users free — admins never counted.
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            to="/app/billing"
            data-testid="trial-countdown-cta"
            className="inline-flex items-center gap-1 px-3 py-1 rounded-sm bg-white text-adb-navy font-semibold hover:bg-zenith-green hover:text-adb-navy transition-colors"
          >
            Subscribe now <ArrowRight size={12} />
          </Link>
          <button
            data-testid="trial-countdown-dismiss"
            onClick={dismiss}
            className="p-1 rounded-sm hover:bg-white/10"
            title="Dismiss for this session"
          >
            <X size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
